/**
 * Field validation, mirroring the CHECK constraints and triggers in the
 * database. The database is the floor — nothing can get under it. This layer
 * exists so the person gets a plain-language message before they hit save,
 * not a Postgres error afterwards.
 *
 * Keep the two in step. If you change a rule here, change it in the schema.
 */

import { parseMoney, type Cents } from "./money";

export type Check = string | null;   // null means valid

const thisYear = () => new Date().getFullYear();

export function checkYear(input: string): Check {
  const v = input.trim();
  if (v === "") return "Enter the model year.";
  if (!/^\d{4}$/.test(v)) return "Enter a four-digit year.";
  const n = Number(v);
  const max = thisYear() + 1;
  if (n < 1935) return "That is before 1935. Check the year.";
  if (n > max) return `Model years only run to ${max}.`;
  return null;
}

export function checkOdometer(input: string): Check {
  const v = input.replace(/,/g, "").trim();
  if (v === "") return "Enter the mileage.";
  if (!/^\d+$/.test(v)) return "Miles only, no decimals.";
  if (Number(v) > 999_999) return "Odometers stop at 999,999.";
  return null;
}

export function checkMoney(input: string, label = "Amount"): Check {
  const raw = input.trim();
  if (raw === "") return null;
  if (raw.startsWith("-")) return `${label} cannot be negative.`;
  if (parseMoney(raw) === null) return "Enter an amount like 8995.00";
  return null;
}

export function checkRequired(input: string, label: string): Check {
  return input.trim() === "" ? `${label} is required.` : null;
}

export function checkPhone(input: string): Check {
  const d = input.replace(/\D/g, "");
  if (d === "") return null;
  if (d.length === 11 && d.startsWith("1")) return null;
  if (d.length !== 10) return "Enter a 10-digit phone number.";
  return null;
}

export function checkEmail(input: string): Check {
  const v = input.trim();
  if (v === "") return null;
  return /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(v) ? null : "That email address is not valid.";
}

export function checkZip(input: string): Check {
  const v = input.trim();
  if (v === "") return "Enter a ZIP code.";
  return /^\d{5}(-?\d{4})?$/.test(v) ? null : "ZIP codes are 5 digits.";
}

/** Buyers must be adults; the database enforces this too. */
export function checkDateOfBirth(iso: string): Check {
  if (iso.trim() === "") return "Enter a date of birth.";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "That is not a valid date.";
  const now = new Date();
  const age = (now.getTime() - d.getTime()) / (365.2425 * 864e5);
  if (age < 18) return "Buyer must be at least 18.";
  if (age > 120) return "Check the year of birth.";
  return null;
}

/** Not fatal — dealers do sell below cost. Worth saying out loud though. */
export function warnAskBelowCost(ask: Cents, cost: Cents): Check {
  if (ask > 0 && cost > 0 && ask < cost)
    return "Asking less than cost. Save it if that is deliberate.";
  return null;
}

/* ------------------------------------------------------------------ */
/* Names, and the numbers a business is known by                       */
/* ------------------------------------------------------------------ */

/**
 * Capitalise a name the way it is printed on a license.
 *
 * Deliberately conservative. It fixes the common cases — a name typed in a
 * hurry, or in caps because the license prints it that way — without
 * inventing capitalisation nobody asked for. Particles like "de" and "van"
 * stay lower case; O'Brien and McLeod keep their internal capitals; anything
 * hyphenated is handled on both sides.
 */
const LOWER_PARTICLES = new Set([
  "de", "del", "de la", "della", "di", "da", "dos", "das",
  "van", "von", "der", "den", "ter", "la", "le", "du", "y", "e",
]);

export function capitalizeName(raw: string): string {
  const s = raw.trim().replace(/\s+/g, " ");
  if (!s) return "";

  const word = (w: string, index: number): string => {
    const lower = w.toLowerCase();

    // Never the first word — "De La Cruz" keeps its capital at the front.
    if (index > 0 && LOWER_PARTICLES.has(lower)) return lower;

    // O'Brien, D'Angelo
    if (/^[od]'/i.test(w) && w.length > 2) {
      return w[0]!.toUpperCase() + "'" + capPart(w.slice(2));
    }
    // McLeod, MacArthur
    if (/^mc/i.test(w) && w.length > 2) return "Mc" + capPart(w.slice(2));
    if (/^mac/i.test(w) && w.length > 4) return "Mac" + capPart(w.slice(3));

    return capPart(w);
  };

  const capPart = (w: string) =>
    w ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w;

  return s.split(" ").map((w, i) =>
    // Hyphenated names get both halves: Smith-Jones, not Smith-jones.
    w.split("-").map((part, j) => word(part, i + j)).join("-")
  ).join(" ");
}

/** Nine digits. Stored without the hyphen, shown with one. */
export function stripEin(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 9);
}

export function formatEin(raw: string): string {
  const d = stripEin(raw);
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
}

export function checkEin(raw: string): string | null {
  const d = stripEin(raw);
  if (!d) return "Enter the EIN.";
  if (d.length !== 9) return `An EIN is nine digits — this has ${d.length}.`;
  return null;
}

/** A plate as Florida prints it: letters and digits, no spaces or dashes. */
export function normalizeTag(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
}

export function checkTag(raw: string): string | null {
  const t = normalizeTag(raw);
  if (!t) return null;                       // blank means "no number to hand"
  if (t.length < 3) return "That looks too short for a plate.";
  return null;
}
